import { ArrowDownUp } from "lucide-react";
import { FC } from "react";
import { useSearchParams } from "react-router";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type Props = {
  columns: { id: string; label: string }[];
  defaultSort?: {
    column: string;
    direction: "asc" | "desc";
  };
};

export const TableSort: FC<Props> = ({
  columns,
  defaultSort = { column: "created_at", direction: "desc" },
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const handleSortColumnChange = (columnId: string) => {
    setSearchParams({ ...Object.fromEntries(searchParams), sort: columnId });
  };

  const handleSortDirectionChange = (direction: "asc" | "desc") => {
    setSearchParams({ ...Object.fromEntries(searchParams), direction });
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline">
          <ArrowDownUp /> Sort
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-90">
        <h4 className="font-medium text-base mb-4">Sort by</h4>
        <div className="flex gap-2">
          <Select
            value={searchParams.get("sort") || defaultSort.column}
            onValueChange={handleSortColumnChange}
          >
            <SelectTrigger
              onPointerDown={(event) => {
                event.stopPropagation();
              }}
            >
              <SelectValue placeholder="Select column" />
            </SelectTrigger>
            <SelectContent
              onCloseAutoFocus={(event) => {
                event.preventDefault();
              }}
            >
              {columns.map((column) => (
                <SelectItem key={column.id} value={column.id}>
                  {column.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={searchParams.get("direction") || defaultSort.direction}
            onValueChange={handleSortDirectionChange}
          >
            <SelectTrigger
              onPointerDown={(event) => {
                event.stopPropagation();
              }}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent
              onCloseAutoFocus={(event) => {
                event.preventDefault();
              }}
            >
              <SelectItem value="asc">Ascending</SelectItem>
              <SelectItem value="desc">Descending</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </PopoverContent>
    </Popover>
  );
};
